"use client";

import { usePathname } from "next/navigation";
import { useMemo } from "react";
import {
  AnalysisTargetInlineMention,
  type AnalysisTargetInlineMentionMetadata,
} from "./analysis-target-display";
import {
  type AnalysisTargetMentionInstance,
  splitAnalysisTargetHighlightSegments,
} from "./analysis-target-mention-token";
import { getAnalysisTargetOptionKey } from "./analysis-target-options";
import { useTextareaOverlayMetrics } from "./use-textarea-overlay-metrics";

type AnalysisTargetInputHighlightProps = {
  textarea: HTMLTextAreaElement | null;
  value: string;
  mentions: AnalysisTargetMentionInstance[];
  optionMetadataByKey: ReadonlyMap<string, AnalysisTargetInlineMentionMetadata>;
};

/**
 * 通常の textarea を維持したまま、メンション部分だけ視覚装飾するオーバーレイ。
 *
 * 選択や IME は実 textarea 側に任せ、オーバーレイは表示専用に限定する。
 */
export function AnalysisTargetInputHighlight({
  textarea,
  value,
  mentions,
  optionMetadataByKey,
}: AnalysisTargetInputHighlightProps) {
  const pathname = usePathname();
  const mentionCount = mentions.length;
  const segments = useMemo(
    () => splitAnalysisTargetHighlightSegments(value, mentions),
    [mentions, value],
  );
  const metrics = useTextareaOverlayMetrics(
    textarea,
    `${pathname}:${mentionCount}`,
  );

  if (!textarea || !value || mentionCount === 0 || metrics.height === 0) {
    return null;
  }

  return (
    <div
      className="pointer-events-none absolute inset-x-0 z-30 overflow-hidden"
      style={{
        top: `${metrics.top}px`,
        height: `${metrics.height}px`,
      }}
    >
      <div
        className="whitespace-pre-wrap break-words"
        style={{
          ...metrics.textStyle,
          transform: `translate(${-metrics.scrollLeft}px, ${-metrics.scrollTop}px)`,
        }}
      >
        {segments.map((segment, index) =>
          segment.kind === "mention" ? (
            <AnalysisTargetInlineMention
              key={`mention-${segment.mention.mentionId}-${index}`}
              target={segment.target}
              metadata={optionMetadataByKey.get(
                getAnalysisTargetOptionKey(segment.target),
              )}
            />
          ) : (
            <span key={`plain-${index}-${segment.text}`}>{segment.text}</span>
          ),
        )}
      </div>
    </div>
  );
}
